// ─── src/hooks/useRegistrations.ts ────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getRegistrations, updateAcceptance, updateRegistration, deleteRegistration } from "../api/registration";
import type { RegistrationsParams, Registration } from "../types/registration";
import type { NormalisedResponse } from "../api/registration";
// Return type is now NormalisedResponse — meta.totalPages is always present

// Query key factory — keeps keys consistent and easy to invalidate
export const registrationKeys = {
  all: ["registrations"] as const,
  list: (params: RegistrationsParams) =>
    [...registrationKeys.all, "list", params] as const,
};

/**
 * Fetches a paginated list of registrations.
 *
 * - keepPreviousData → no layout jump while fetching the next page
 * - staleTime 30 s  → avoids hammering the API on rapid re-renders
 */
export const useRegistrations = (params: RegistrationsParams) =>
  useQuery({
    queryKey: registrationKeys.list(params),
    queryFn: () => getRegistrations(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useUpdateAcceptance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: string; accepted: boolean }) =>
      updateAcceptance(id, accepted),

    // Optimistically update the status badge immediately — no waiting for refetch
    onMutate: async ({ id, accepted }) => {
      await qc.cancelQueries({ queryKey: registrationKeys.all });

      const newStatus = accepted ? "accepted" : "rejected";
      const queries = qc.getQueriesData<NormalisedResponse>({ queryKey: registrationKeys.all });

      queries.forEach(([key, old]) => {
        if (!old) return;
        qc.setQueryData<NormalisedResponse>(key, {
          ...old,
          data: old.data.map((r) =>
            r.id === id ? { ...r, status: newStatus, accepted } : r
          ),
        });
      });

      return { queries }; // snapshot for rollback
    },

    // Roll back on error
    onError: (_err, _vars, ctx) => {
      ctx?.queries.forEach(([key, old]) => qc.setQueryData(key, old));
    },

    // Always sync with server after settle
    onSettled: () => qc.invalidateQueries({ queryKey: registrationKeys.all }),
  });
};

export const useUpdateRegistration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<Registration> }) =>
      updateRegistration(id, dto),

    onMutate: async ({ id, dto }) => {
      await qc.cancelQueries({ queryKey: registrationKeys.all });
      const queries = qc.getQueriesData<NormalisedResponse>({ queryKey: registrationKeys.all });
      queries.forEach(([key, old]) => {
        if (!old) return;
        qc.setQueryData<NormalisedResponse>(key, {
          ...old,
          data: old.data.map((r) => (r.id === id ? { ...r, ...dto } : r)),
        });
      });
      return { queries };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.queries.forEach(([key, old]) => qc.setQueryData(key, old));
    },

    onSettled: () => qc.invalidateQueries({ queryKey: registrationKeys.all }),
  });
};

export const useDeleteRegistration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRegistration(id),

    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: registrationKeys.all });
      const queries = qc.getQueriesData<NormalisedResponse>({ queryKey: registrationKeys.all });
      queries.forEach(([key, old]) => {
        if (!old) return;
        qc.setQueryData<NormalisedResponse>(key, {
          ...old,
          data: old.data.filter((r) => r.id !== id),
        });
      });
      return { queries };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.queries.forEach(([key, old]) => qc.setQueryData(key, old));
    },

    onSettled: () => qc.invalidateQueries({ queryKey: registrationKeys.all }),
  });
};